/**
 * The left navigation.
 *
 * Built from the permissions the server returned at sign-in, so the menu can
 * never offer a screen the API would refuse. Adding a screen is a ROUTE row and
 * a route in App.jsx - not an edit here.
 *
 * A route with a parent renders indented under it, which is how Purchases and
 * Recipes sit beneath Inventory: the top level stays short without anything
 * becoming unreachable.
 *
 * The card at the foot follows the layout - photo, name, designation, the date
 * and time, the day, and the weather.
 *
 * Two shapes. From `lg` up it is a column beside the content, either full width
 * or collapsed to its icons. Below `lg` there is no width to give it, so the
 * same list is rendered as an off-canvas drawer over the content - see
 * AppLayout, which owns the backdrop. `collapsed` is meaningless in the drawer
 * (a drawer that is open is open at full width), so the drawer always passes
 * false, and tapping any entry closes it: on a phone the user cannot see the
 * screen they just navigated to underneath it.
 */

import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Fi from 'react-icons/fi';

import { selectMenuItems, selectUser, logout } from '../store/authSlice';
import { selectFiscal, selectShift } from '../store/uiSlice';
import { selectBusiness } from '../store/menuSlice';
import { formatDate, initials } from '../utils/format';
import useWeather from '../hooks/useWeather';

/** Resolve the react-icons name stored on the ROUTE row. */
const iconFor = (name) => Fi[name] || Fi.FiCircle;

const Sidebar = ({ collapsed, onNavigate = null }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const menuItems = useSelector(selectMenuItems);
    const user = useSelector(selectUser);
    const business = useSelector(selectBusiness);
    const fiscal = useSelector(selectFiscal);
    const shift = useSelector(selectShift);
    const weather = useWeather();

    // The clock is part of the layout, so it has to actually tick.
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    /**
     * Settings sits on its own at the foot, away from the working screens, so
     * nobody reaches for it by accident during service.
     */
    const footerCodes = new Set(['SETTINGS']);
    const mainItems = menuItems.filter((item) => !footerCodes.has(item.code));
    const footerItems = menuItems.filter((item) => footerCodes.has(item.code));

    const onLogout = async () => {
        if (shift && !window.confirm('A shift is still open on this till. Sign out anyway?')) return;
        await dispatch(logout());
        navigate('/login');
    };

    const renderItem = (item) => {
        const Icon = iconFor(item.icon);
        const isChild = !!item.parentRouteId;

        // The fiscal entry carries a count of what is waiting to be reported
        const badgeCount = item.code === 'FISCAL'
            ? (fiscal.failed || 0) + (fiscal.pending || 0)
            : 0;
        const badgeTone = fiscal.failed > 0 ? 'bg-red-500' : 'bg-amber-500';

        return (
            <li key={item.code} className="mb-1">
                <NavLink
                    to={item.path}
                    onClick={onNavigate || undefined}
                    title={collapsed ? item.name : undefined}
                    className={({ isActive }) =>
                        `relative flex items-center gap-3 rounded-xl text-sm transition-all duration-200
                         ${collapsed ? 'justify-center px-3' : isChild ? 'pl-10 pr-3' : 'px-3'}
                         ${isChild ? 'min-h-[2.5rem] lg:min-h-[2.25rem] font-medium' : 'min-h-[3rem] lg:min-h-[2.75rem] font-semibold'}
                         ${isActive
                            ? 'bg-brand-50 text-brand-700 shadow-sm ring-1 ring-brand-700/10'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`
                    }
                >
                    {({ isActive }) => (
                        <>
                            {isActive && !collapsed && (
                                <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r bg-brand-700" />
                            )}
                            <Icon
                                className={`${isChild ? 'h-4 w-4 shrink-0' : 'h-5 w-5 shrink-0'}
                                            ${isActive ? 'text-brand-700' : 'text-slate-400 group-hover:text-slate-600'}`}
                            />
                            {!collapsed && <span className="truncate">{item.name}</span>}

                            {badgeCount > 0 && (
                                <span
                                    className={`${collapsed ? 'absolute right-1 top-1' : 'ml-auto'}
                                                tabular rounded-full ${badgeTone} px-1.5 py-0.5
                                                text-[10px] font-black text-white shadow-sm`}
                                >
                                    {badgeCount > 99 ? '99+' : badgeCount}
                                </span>
                            )}
                        </>
                    )}
                </NavLink>
            </li>
        );
    };

    return (
        <aside
            className={`flex h-full w-full shrink-0 flex-col border-r border-slate-200/80 bg-white shadow-[2px_0_8px_-4px_rgba(0,0,0,0.05)] transition-[width] duration-200
                        ${collapsed ? 'lg:w-[72px]' : 'lg:w-64'}`}
        >
            {/* ── Brand Header ────────────────────────────────────────── */}
            <div
                className="flex h-14 shrink-0 items-center gap-3 px-4"
                style={{ backgroundColor: '#0f1f5c' }}
            >
                <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/20 text-white shadow-sm"
                >
                    {business?.Logo_Path
                        ? <img src={business.Logo_Path} alt="" className="h-full w-full object-contain" />
                        : <Fi.FiShoppingBag className="h-4 w-4" />}
                </div>
                {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-white">
                            {business?.Business_Name || 'Nexus POS'}
                        </p>
                    </div>
                )}

                {/* Drawer only. The backdrop closes it too, but a phone held in
                    one hand cannot always reach the strip beside the drawer. */}
                {onNavigate && (
                    <button
                        type="button"
                        onClick={onNavigate}
                        className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg
                                   text-white/70 transition-colors hover:bg-white/15 hover:text-white lg:hidden"
                        aria-label="Close menu"
                    >
                        <Fi.FiX className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* ── Navigation ──────────────────────────────────────────── */}
            <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4 scrollbar-none">
                <ul className="space-y-0.5">{mainItems.map(renderItem)}</ul>

                {footerItems.length > 0 && (
                    <>
                        <hr className="my-4 border-slate-100" />
                        <ul className="space-y-0.5">{footerItems.map(renderItem)}</ul>
                    </>
                )}

                {/* Logout Action */}
                {/* <ul className="mt-1 space-y-0.5">
                    <li>
                        <button
                            type="button"
                            onClick={onLogout}
                            title={collapsed ? 'Logout' : undefined}
                            className={`group flex w-full items-center gap-3 rounded-xl min-h-[2.75rem] text-sm font-semibold
                                        text-slate-500 transition-all duration-200 hover:bg-red-50 hover:text-red-700
                                        ${collapsed ? 'justify-center px-3' : 'px-3'}`}
                        >
                            <Fi.FiLogOut className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-red-500" />
                            {!collapsed && <span>Logout</span>}
                        </button>
                    </li>
                </ul> */}
            </nav>

            {/* ── Footer Info Card ────────────────────────────────────── */}
            <div className="border-t border-slate-100 p-2">
                {!collapsed && (
                    <div
                        className="mb-1 rounded-xl p-2.5 text-white shadow-md relative overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, #0f1f5c 0%, #152fe1 100%)' }}
                    >
                        {/* Subtle glow effect behind */}
                        <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-white/10 blur-xl pointer-events-none" />

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60 mb-0.5">
                                    {now.toLocaleDateString('en-PK', { weekday: 'long' })}
                                </p>
                                <p className="tabular text-xs font-medium text-white/80">
                                    {formatDate(now)}
                                </p>
                                <p className="tabular text-lg font-bold tracking-tight leading-none mt-0.5">
                                    {now.toLocaleTimeString('en-PK', {
                                        hour: 'numeric', minute: '2-digit', hour12: true
                                    })}
                                </p>
                            </div>

                            {weather && (
                                <div className="text-right flex flex-col items-end" title={weather.description}>
                                    <weather.Icon className="h-6 w-6 text-white/90 mb-0.5" />
                                    <span className="text-sm font-bold">{weather.temperature}°</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* 
                <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full
                                    bg-brand-100 border border-brand-200/60 text-sm font-black text-brand-700 shadow-sm">
                        {user?.photoPath
                            ? <img src={user.photoPath} alt="" className="h-full w-full object-cover" />
                            : initials(user?.displayName)}
                    </div>

                    {!collapsed && (
                        <div className="min-w-0">
                            <p className="truncate text-[13px] font-bold text-slate-800">
                                {user?.displayName || user?.userName}
                            </p>
                            <p className="truncate text-[11px] font-medium text-slate-400 capitalize">
                                {user?.designation || user?.roles?.[0] || 'User'}
                            </p>
                        </div>
                    )}
                </div> */}
            </div>
        </aside>
    );
};

export default Sidebar;
